import React, { useEffect, useMemo, useRef, useState } from "react";
import {
    Shield,
    AlertTriangle,
    DoorOpen,
    Siren,
    Search,
    CheckCircle2,
    XCircle,
    ChevronDown,
} from "lucide-react";
import { io } from "socket.io-client";
import api from "../utils/api";
import "../styles/CenterCommand.css";
import PageQuickNav from "../components/PageQuickNav";


const CenterCommand = () => {
    const [activeTab, setActiveTab] = useState("overview");

    const [safety, setSafety] = useState(null);
    const [security, setSecurity] = useState(null);
    const [trustedDevices, setTrustedDevices] = useState([]);
    const [releaseHistory, setReleaseHistory] = useState([]);
    const [evacuation, setEvacuation] = useState({
        active: false,
        session: null,
        summary: null,
    });


    const storedUser = JSON.parse(localStorage.getItem("kuddleUser") || "{}");

    const activeCenterId =
        storedUser.currentCenterId || storedUser.primaryCenterId || null;

    const [ratioAlerts, setRatioAlerts] = useState([]);
    const [loading, setLoading] = useState(false);
    const [statusMsg, setStatusMsg] = useState("");
    const [error, setError] = useState("");
    const [riskFeed, setRiskFeed] = useState([]);
    const [emergencyState, setEmergencyState] = useState(null);
    const [confirmEmergencyType, setConfirmEmergencyType] = useState("");
    const [studentQuery, setStudentQuery] = useState("");
    const [studentResults, setStudentResults] = useState([]);
    const [showStudentResults, setShowStudentResults] = useState(false);
    const [highlightIndex, setHighlightIndex] = useState(-1);
    const studentSearchRef = useRef(null);


    const [releaseForm, setReleaseForm] = useState({
        studentId: "",
        releasedToName: "",
        releasedToRelationship: "",
        releasedToPhone: "",
        releasedToEmail: "",
        pickupCode: "",
        verificationMethod: "manual_verification",
        notes: "",
        override: false,
        overrideReason: "",
    });

    const [historyFilters, setHistoryFilters] = useState({
        q: "",
        status: "",
        method: "",
        overrideUsed: "",
    });

    const [evacuationType, setEvacuationType] = useState("drill");
    const [evacuationNotes, setEvacuationNotes] = useState("");
    const [evacuationHistory, setEvacuationHistory] = useState([]);
    const [evacuationHistoryPagination, setEvacuationHistoryPagination] = useState({
        page: 1,
        limit: 10,
        total: 0,
        pages: 1,
        hasNext: false,
        hasPrev: false,
    });

    const [evacuationHistoryFilters, setEvacuationHistoryFilters] = useState({
        type: "",
        status: "",
        startDate: "",
        endDate: "",
    });

    const [evacuationHistoryPage, setEvacuationHistoryPage] = useState(1);

    const loadSafety = async () => {
        try {
            const res = await api.get("/api/dashboard/safety", {
                params: { centerId: activeCenterId },
            });
            setSafety(res.data || {});
        } catch (err) {
            console.error("Safety error:", err);
            setSafety({});
            setError("Failed to load safety dashboard");
        }
    };

    const loadSecurity = async () => {
        try {
            const res = await api.get("/api/dashboard/security", {
                params: { centerId: activeCenterId },
            });
            setSecurity(res.data || {});
        } catch (err) {
            console.error("Security error:", err);
            setSecurity({});
            setError("Failed to load security dashboard");
        }
    };

    const loadTrustedDevices = async () => {
        try {
            const res = await api.get("/api/auth/trusted-devices");
            setTrustedDevices(Array.isArray(res.data?.devices) ? res.data.devices : []);
        } catch (err) {
            console.error("Trusted devices error:", err);
            setTrustedDevices([]);
        }
    };

    const removeTrustedDevice = async (deviceId) => {
        if (!window.confirm("Remove this trusted device?")) return;

        try {
            await api.delete(`/api/auth/trusted-devices/${deviceId}`);
            await loadTrustedDevices();
            setStatusMsg("Trusted device removed.");
        } catch (err) {
            console.error("Remove trusted device error:", err);
            setError("Failed to remove trusted device.");
        }
    };

    const removeAllTrustedDevices = async () => {
        if (!window.confirm("Remove all trusted devices from your account?")) return;

        try {
            await api.delete("/api/auth/trusted-devices");
            await loadTrustedDevices();
            setStatusMsg("All trusted devices removed.");
        } catch (err) {
            console.error("Remove all trusted devices error:", err);
            setError("Failed to remove trusted devices.");
        }
    };

    const [releaseSummary, setReleaseSummary] = useState({
        completed: 0,
        blocked: 0,
        totalMatching: 0,
    });

    const [releasePagination, setReleasePagination] = useState({
        page: 1,
        limit: 10,
        total: 0,
        pages: 1,
        hasNext: false,
        hasPrev: false,
    });

    const [historyPage, setHistoryPage] = useState(1);
    const [historyLimit, setHistoryLimit] = useState(10);

    const loadReleaseHistory = async () => {
        try {
            const params = new URLSearchParams();

            if (activeCenterId) params.set("centerId", activeCenterId);
            if (historyFilters.q) params.set("q", historyFilters.q);
            if (historyFilters.status) params.set("status", historyFilters.status);
            if (historyFilters.method) params.set("method", historyFilters.method);
            if (historyFilters.overrideUsed) {
                params.set("overrideUsed", historyFilters.overrideUsed);
            }

            params.set("page", historyPage);
            params.set("limit", historyLimit);

            const query = params.toString();

            const res = await api.get(
                `/api/child-release/history${query ? `?${query}` : ""}`
            );

            setReleaseHistory(Array.isArray(res.data?.rows) ? res.data.rows : []);
            setReleaseSummary(
                res.data?.summary || { completed: 0, blocked: 0, totalMatching: 0 }
            );
            setReleasePagination(
                res.data?.pagination || {
                    page: 1,
                    limit: 10,
                    total: 0,
                    pages: 1,
                    hasNext: false,
                    hasPrev: false,
                }
            );
        } catch (err) {
            console.error("Child release history error:", err);
            setReleaseHistory([]);
            setReleaseSummary({ completed: 0, blocked: 0, totalMatching: 0 });
            setReleasePagination({
                page: 1,
                limit: 10,
                total: 0,
                pages: 1,
                hasNext: false,
                hasPrev: false,
            });
            setError("Failed to load child release history");
        }
    };

    const loadEvacuation = async () => {
        try {
            const res = await api.get("/api/evacuation/active", {
                params: { centerId: activeCenterId },
            });
            setEvacuation(
                res.data || {
                    active: false,
                    session: null,
                    summary: {
                        total: 0,
                        evacuated: 0,
                        missing: 0,
                        pending: 0,
                    },
                }
            );
        } catch (err) {
            console.error("Evacuation error:", err);
            setEvacuation({
                active: false,
                session: null,
                summary: {
                    total: 0,
                    evacuated: 0,
                    missing: 0,
                    pending: 0,
                },
            });
            setError("Failed to load evacuation dashboard");
        }
    };

    const loadEvacuationHistory = async () => {
        try {
            const params = new URLSearchParams();

            if (evacuationHistoryFilters.type) {
                params.set("type", evacuationHistoryFilters.type);
            }

            if (evacuationHistoryFilters.status) {
                params.set("status", evacuationHistoryFilters.status);
            }

            if (evacuationHistoryFilters.startDate) {
                params.set("startDate", evacuationHistoryFilters.startDate);
            }

            if (evacuationHistoryFilters.endDate) {
                params.set("endDate", evacuationHistoryFilters.endDate);
            }

            if (activeCenterId) {
                params.set("centerId", activeCenterId);
            }

            params.set("page", evacuationHistoryPage);
            params.set("limit", "10");

            const query = params.toString();
            const res = await api.get(`/api/evacuation/history?${query}`);

            setEvacuationHistory(Array.isArray(res.data?.sessions) ? res.data.sessions : []);
            setEvacuationHistoryPagination(
                res.data?.pagination || {
                    page: 1,
                    limit: 10,
                    total: 0,
                    pages: 1,
                    hasNext: false,
                    hasPrev: false,
                }
            );
        } catch (err) {
            console.error("Evacuation history error:", err);
            setEvacuationHistory([]);
            setEvacuationHistoryPagination({
                page: 1,
                limit: 10,
                total: 0,
                pages: 1,
                hasNext: false,
                hasPrev: false,
            });
            setError("Failed to load evacuation history");
        }
    };

    useEffect(() => {
        if (activeTab === "reports") {
            loadEvacuationHistory();
        }
    }, [activeTab, evacuationHistoryFilters, evacuationHistoryPage, activeCenterId]);

    const loadEmergencyState = async () => {
        try {
            const res = await api.get("/api/emergency", {
                params: { centerId: activeCenterId },
            });

            setEmergencyState(res.data || null);
        } catch (err) {
            console.error("Emergency state error:", err);
            setEmergencyState(null);
            setError("Failed to load emergency state");
        }
    };

    const loadRiskFeed = async () => {
        try {
            const res = await api.get("/api/dashboard/risk-feed", {
                params: { centerId: activeCenterId },
            });

            const data = res.data || {};
            setRiskFeed(Array.isArray(data.risks) ? data.risks : []);
            setRatioAlerts(Array.isArray(data.ratios) ? data.ratios : []);
        } catch (err) {
            console.error("Risk feed error:", err);
            setRiskFeed([]);
            setRatioAlerts([]);
            setError("Failed to load risk feed");
        }
    };

    const loadAll = async () => {
        try {
            setLoading(true);
            setError("");
            await Promise.all([
                loadSafety(),
                loadSecurity(),
                loadTrustedDevices(),
                loadReleaseHistory(),
                loadEvacuation(),
                loadRiskFeed(),
                loadEmergencyState(),
            ]);
        } catch (err) {
            console.error("Command center load error:", err);
            setError("Failed to load command center data");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        const runSearch = async () => {
            try {
                const query = studentQuery.trim();
                const res = await api.get(
                    `/api/students/search?q=${encodeURIComponent(query)}`, {
                    params: { centerId: activeCenterId },
                }
                );

                setStudentResults(Array.isArray(res.data) ? res.data : []);
            } catch (err) {
                console.error("Student search failed:", err);
                setStudentResults([]);
            }
        };

        const timer = setTimeout(runSearch, 250);
        return () => clearTimeout(timer);
    }, [studentQuery, activeCenterId]);

    useEffect(() => {
        loadAll();
    }, [activeCenterId]);

    useEffect(() => {
        loadReleaseHistory();
    }, [historyFilters, historyPage, historyLimit, activeCenterId]);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (
                studentSearchRef.current &&
                !studentSearchRef.current.contains(event.target)
            ) {
                setShowStudentResults(false);
            }
        };

        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, []);

    useEffect(() => {
        const socket = io(process.env.REACT_APP_SOCKET_URL || "", {
            transports: ["websocket"],
        });

        const reloadCommandCenter = async () => {
            try {
                await Promise.all([
                    loadSafety(),
                    loadSecurity(),
                    loadEvacuation(),
                    loadRiskFeed(),
                    loadEmergencyState(),
                ]);
            } catch (err) {
                console.error("Live reload failed:", err);
            }
        };

        socket.on("attendance:update", reloadCommandCenter);
        socket.on("ratio:alert", reloadCommandCenter);
        socket.on("evacuation:update", reloadCommandCenter);
        socket.on("emergency:update", reloadCommandCenter);

        return () => {
            socket.off("attendance:update", reloadCommandCenter);
            socket.off("ratio:alert", reloadCommandCenter);
            socket.off("evacuation:update", reloadCommandCenter);
            socket.off("emergency:update", reloadCommandCenter);
            socket.disconnect();
        };
    }, []);

    const handleReleaseChange = (field, value) => {
        setReleaseForm((prev) => ({ ...prev, [field]: value }));
    };

    const activateEmergencyMode = async (type = "evacuation") => {
        try {
            await api.post("/api/emergency/activate", {
                centerId: activeCenterId,
                type,
                message: `Emergency mode activated: ${type}`,
            });

            setStatusMsg("Emergency mode activated.");
            await Promise.all([loadEmergencyState(), loadRiskFeed()]);
        } catch (err) {
            console.error(err);
            setError(
                err?.response?.data?.message || "Failed to activate emergency mode"
            );
        }
    };

    const deactivateEmergencyMode = async () => {
        try {
            await api.post("/api/emergency/deactivate", {
                centerId: activeCenterId,
            });
            setStatusMsg("Emergency mode deactivated.");
            await Promise.all([loadEmergencyState(), loadRiskFeed()]);
        } catch (err) {
            console.error(err);
            setError(
                err?.response?.data?.message || "Failed to deactivate emergency mode"
            );
        }
    };

    const submitRelease = async (e) => {
        e.preventDefault();
        setStatusMsg("");
        setError("");

        try {
            await api.post("/api/child-release/release", {
                centerId: activeCenterId,
                studentId: releaseForm.studentId,
                releasedTo: {
                    name: releaseForm.releasedToName,
                    relationship: releaseForm.releasedToRelationship,
                    phone: releaseForm.releasedToPhone,
                    email: releaseForm.releasedToEmail,
                },
                pickupCode: releaseForm.pickupCode,
                verificationMethod: releaseForm.verificationMethod,
                notes: releaseForm.notes,
                override: releaseForm.override,
                overrideReason: releaseForm.overrideReason,
            });

            setStatusMsg("Child release completed successfully.");
            setReleaseForm({
                studentId: "",
                releasedToName: "",
                releasedToRelationship: "",
                releasedToPhone: "",
                releasedToEmail: "",
                pickupCode: "",
                verificationMethod: "manual_verification",
                notes: "",
                override: false,
                overrideReason: "",
            });

            setStudentQuery("");
            setStudentResults([]);
            setShowStudentResults(false);
            setHighlightIndex(-1);

            await Promise.all([loadReleaseHistory(), loadSafety()]);
        } catch (err) {
            console.error(err);
            setError(err?.response?.data?.error || "Child release failed");
        }
    };

    const startEvacuation = async () => {
        setStatusMsg("");
        setError("");

        try {
            await api.post("/api/evacuation/start", {
                centerId: activeCenterId,
                type: evacuationType,
                notes: evacuationNotes,
            });

            setStatusMsg("Evacuation session started.");
            await loadEvacuation();
        } catch (err) {
            console.error(err);
            setError(
                err?.response?.data?.error || "Failed to start evacuation session"
            );
        }
    };

    const markEvacuationStudent = async (studentId, status) => {
        if (!evacuation?.session?._id) return;

        try {
            await api.post(`/api/evacuation/${evacuation.session._id}/mark-student`, {
                centerId: activeCenterId,
                studentId,
                status,
            });

            await loadEvacuation();
        } catch (err) {
            console.error(err);
            setError(
                err?.response?.data?.error || "Failed to update evacuation status"
            );
        }
    };

    const completeEvacuation = async () => {
        try {
            if (!evacuation?.session?._id) return;

            await api.post(`/api/evacuation/${evacuation.session._id}/complete`, {
                centerId: activeCenterId,
            });

            setError("");
            setStatusMsg("Evacuation session completed.");

            setTimeout(() => {
                setStatusMsg("");
            }, 3000);

            await loadEvacuation();
            await loadSafety();
            await loadRiskFeed();
            await loadEmergencyState();

            setEvacuationType("drill");
            setEvacuationNotes("");

            setActiveTab("overview");
        } catch (err) {
            console.error("Complete evacuation failed:", err);
            setError(err?.response?.data?.error || "Failed to complete evacuation session");
        }
    };

    const roomAlerts = useMemo(
        () => ratioAlerts.filter((r) => r.ratioExceeded),
        [ratioAlerts]
    );
    const nearAlerts = useMemo(
        () => ratioAlerts.filter((r) => !r.ratioExceeded && r.nearRatioLimit),
        [ratioAlerts]
    );

    const exportReleaseHistory = async () => {
        try {
            const params = new URLSearchParams();

            if (activeCenterId) {
                params.set("centerId", activeCenterId);
            }

            if (historyFilters.q) params.set("q", historyFilters.q);
            if (historyFilters.status) params.set("status", historyFilters.status);
            if (historyFilters.method) params.set("method", historyFilters.method);
            if (historyFilters.overrideUsed) {
                params.set("overrideUsed", historyFilters.overrideUsed);
            }

            params.set("export", "csv");

            const res = await api.get(
                `/api/child-release/history/export?${params.toString()}`,
                { responseType: "blob" }
            );

            const blob = new Blob([res.data], { type: "text/csv;charset=utf-8;" });
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement("a");

            link.href = url;
            link.download = `release-history-${new Date().toISOString().slice(0, 10)}.csv`;
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
        } catch (err) {
            console.error("Release history export failed:", err);
            setError("Failed to export release history");
        }
    };

    const exportEvacuationReport = async (sessionId) => {
        try {
            const res = await api.get(
                `/api/evacuation/${sessionId}/export`,
                {
                    params: { centerId: activeCenterId },
                    responseType: "blob",
                }
            );


            const blob = new Blob([res.data], { type: "text/csv;charset=utf-8;" });
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement("a");

            link.href = url;
            link.download = `evacuation-report-${sessionId}.csv`;
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
        } catch (err) {
            console.error("Evacuation export failed:", err);
            setError("Failed to export evacuation report");
        }
    };

    return (
        <div className="command-page">
            <div className="command-topbar">
                <div className="command-header">
                    <div className="page-header-row">
                        <div>
                            <h1>Center Command</h1>
                            <p>Safety, security, release, and evacuation controls in one place.</p>
                        </div>

                        <PageQuickNav showHome />
                    </div>
                </div>


                {statusMsg && <div className="command-banner success">{statusMsg}</div>}
                {error && <div className="command-banner error">{error}</div>}
            </div>

            <section className={`emergency-banner ${emergencyState?.isActive ? "active" : ""}`}>
                <div className="emergency-banner-left">
                    <h2>Global Emergency Mode</h2>
                    <p>
                        {emergencyState?.isActive
                            ? `ACTIVE — ${emergencyState.type}${emergencyState.message ? ` • ${emergencyState.message}` : ""}`
                            : "No emergency currently active"}
                    </p>

                    <div className="emergency-accountability">
                        <span>Students Present: {emergencyState?.accountability?.studentsPresent ?? 0}</span>
                        <span>Staff On Site: {emergencyState?.accountability?.staffOnSite ?? 0}</span>
                        <span>Evacuated: {emergencyState?.accountability?.evacuated ?? 0}</span>
                        <span>Missing: {emergencyState?.accountability?.missing ?? 0}</span>
                        <span>Pending: {emergencyState?.accountability?.pending ?? 0}</span>
                    </div>
                </div>


                <div className="emergency-actions-group">
                    {!emergencyState?.isActive ? (
                        <>
                            <div className="emergency-action-block">
                                <span className="emergency-action-label">🔥 Fire / Evacuation</span>
                                <button
                                    type="button"
                                    className="emergency-btn danger"
                                    onClick={() => setConfirmEmergencyType("evacuation")}
                                >
                                    Activate Evacuation
                                </button>
                            </div>

                            <div className="emergency-action-block">
                                <span className="emergency-action-label">🔒 Security Lockdown</span>
                                <button
                                    type="button"
                                    className="emergency-btn warning"
                                    onClick={() => setConfirmEmergencyType("lockdown")}
                                >
                                    Activate Lockdown
                                </button>
                            </div>
                        </>
                    ) : (
                        <div className="emergency-action-block">
                            <span className="emergency-action-label">✅ Resolve Emergency</span>
                            <button
                                type="button"
                                className="emergency-btn success"
                                onClick={deactivateEmergencyMode}
                            >
                                Deactivate Emergency
                            </button>
                        </div>
                    )}
                </div>
            </section>

            <section className="command-grid-top">
                <div className="command-panel risk-panel">
                    <div className="risk-feed-header">
                        <h2>Live Risk Feed</h2>
                        <p>Highest-priority issues across safety, security, release, and evacuation.</p>
                    </div>

                    {riskFeed.length === 0 ? (
                        <div className="risk-feed-ok">
                            <CheckCircle2 size={18} />
                            All systems normal
                        </div>
                    ) : (
                        <div className="risk-feed-list">
                            {riskFeed.map((risk, index) => (
                                <div
                                    key={`${risk.type}-${index}`}
                                    className={`risk-feed-item ${risk.severity}`}
                                >
                                    <div className="risk-feed-top">
                                        <strong>{risk.title}</strong>
                                        <span
                                            className={`badge ${risk.severity === "critical" || risk.severity === "high"
                                                ? "danger"
                                                : "warning"
                                                }`}
                                        >
                                            {risk.severity}
                                        </span>
                                    </div>
                                    <p>{risk.message}</p>
                                </div>
                            ))}
                        </div>
                    )}
                </div>

                <div className="command-panel building-panel">
                    <div className="panel-header-inline">
                        <div>
                            <h2>Building Status</h2>
                            <p>Live room and attendance safety overview.</p>
                        </div>
                    </div>

                    <div className="command-cards compact">
                        <div className="command-card">
                            <h3>Students Present</h3>
                            <strong>{safety?.studentsPresent ?? 0}</strong>
                        </div>
                        <div className="command-card">
                            <h3>Checked Out</h3>
                            <strong>{safety?.studentsCheckedOut ?? 0}</strong>
                        </div>
                        <div className="command-card">
                            <h3>Still In Building</h3>
                            <strong>{safety?.studentsStillInBuilding ?? 0}</strong>
                        </div>
                        <div className="command-card">
                            <h3>Late Pickups</h3>
                            <strong>{safety?.latePickups ?? 0}</strong>
                        </div>
                    </div>

                    {(roomAlerts.length > 0 || nearAlerts.length > 0) && (
                        <div className="ratio-alert-strip">
                            {roomAlerts.map((room) => (
                                <div key={room.classroomId} className="ratio-alert danger">
                                    <AlertTriangle size={16} />
                                    <span>
                                        {room.name}: {room.present} present / {room.allowedByRatio} allowed
                                    </span>
                                </div>
                            ))}

                            {nearAlerts.map((room) => (
                                <div key={room.classroomId} className="ratio-alert warning">
                                    <AlertTriangle size={16} />
                                    <span>
                                        {room.name}: nearing ratio limit ({room.present}/{room.allowedByRatio})
                                    </span>
                                </div>
                            ))}
                        </div>
                    )}

                    <div className="room-grid">
                        {(safety?.rooms || []).map((room) => (
                            <div
                                key={room.classroomId}
                                className={`room-card ${room.ratioExceeded ? "danger" : room.nearCapacity ? "warning" : ""}`}
                            >
                                <h4>{room.name}</h4>
                                <p>Present: {room.present}</p>
                                <p>Capacity: {room.capacity}</p>
                                <p>Teachers: {room.teachers ?? 0}</p>
                                <p>Allowed by Ratio: {room.allowedByRatio ?? 0}</p>
                                {!room.ratioExceeded && room.nearCapacity && <span className="badge warning">Near Capacity</span>}
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            <div className="command-tabs">
                <button
                    className={activeTab === "overview" ? "active" : ""}
                    onClick={() => setActiveTab("overview")}
                >
                    Overview
                </button>
                <button
                    className={activeTab === "security" ? "active" : ""}
                    onClick={() => setActiveTab((prev) => (prev === "security" ? "overview" : "security"))}
                >
                    <Shield size={16} />
                    Security
                </button>

                <button
                    className={activeTab === "release" ? "active" : ""}
                    onClick={() => setActiveTab((prev) => (prev === "release" ? "overview" : "release"))}
                >
                    <DoorOpen size={16} />
                    Child Release
                </button>

                <button
                    className={activeTab === "evacuation" ? "active" : ""}
                    onClick={() => setActiveTab((prev) => (prev === "evacuation" ? "overview" : "evacuation"))}
                >
                    <Siren size={16} />
                    Evacuation
                </button>

                <button
                    className={activeTab === "reports" ? "active" : ""}
                    onClick={() => setActiveTab((prev) => (prev === "reports" ? "overview" : "reports"))}
                >
                    Reports
                </button>
            </div>

            {
                activeTab === "security" && (
                    <div className="command-section">
                        <div className="command-cards metrics-grid">
                            <div className="command-card metric-card"><h3>Failed Logins</h3><strong>{security?.failedLogins ?? 0}</strong></div>
                            <div className="command-card metric-card"><h3>Successful Logins</h3><strong>{security?.successfulLogins ?? 0}</strong></div>
                            <div className="command-card metric-card"><h3>Account Lockouts</h3><strong>{security?.accountLockouts ?? 0}</strong></div>
                            <div className="command-card metric-card"><h3>2FA Failures</h3><strong>{security?.twoFactorFailures ?? 0}</strong></div>
                            <div className="command-card metric-card"><h3>Device Trust Requests</h3><strong>{security?.deviceTrustRequests ?? 0}</strong></div>
                            <div className="command-card metric-card"><h3>Devices Trusted</h3><strong>{security?.devicesTrusted ?? 0}</strong></div>
                            <div className="command-card metric-card"><h3>Password Reset Requests</h3><strong>{security?.passwordResetRequests ?? 0}</strong></div>
                            <div className="command-card metric-card"><h3>Attendance Overrides</h3><strong>{security?.attendanceOverrides ?? 0}</strong></div>
                            <div className="command-card metric-card"><h3>Pickup Codes Used</h3><strong>{security?.pickupCodesUsed ?? 0}</strong></div>
                            <div className="command-card metric-card"><h3>Exports Performed</h3><strong>{security?.exportsPerformed ?? 0}</strong></div>
                        </div>
                        <div className="command-panel" style={{ marginTop: 24 }}>
                            <div className="panel-header-inline">
                                <div>
                                    <h2>Trusted Devices</h2>
                                    <p>Devices remembered for 30 days for your account.</p>
                                </div>

                                <button
                                    type="button"
                                    className="danger-btn"
                                    onClick={removeAllTrustedDevices}
                                    disabled={trustedDevices.length === 0}
                                >
                                    Remove All
                                </button>
                            </div>

                            {trustedDevices.length === 0 ? (
                                <div className="empty-state">
                                    No trusted devices found.
                                </div>
                            ) : (
                                <div className="history-list">
                                    {trustedDevices.map((device) => (
                                        <div key={device._id} className="history-item completed">
                                            <div className="history-top">
                                                <strong>
                                                    {device.userAgent?.includes("Chrome")
                                                        ? "Windows PC • Chrome"
                                                        : device.userAgent?.includes("Edg")
                                                            ? "Windows PC • Edge"
                                                            : device.userAgent?.includes("Safari")
                                                                ? "Apple Device • Safari"
                                                                : "Trusted Device"}
                                                </strong>
                                                <span className="badge success">Trusted</span>
                                            </div>

                                            <p>
                                                Trusted: {device.trustedAt ? new Date(device.trustedAt).toLocaleString() : "—"}
                                            </p>
                                            <p>
                                                Last Seen: {device.lastSeenAt ? new Date(device.lastSeenAt).toLocaleString() : "—"}
                                            </p>
                                            <p>
                                                Expires: {device.expiresAt ? new Date(device.expiresAt).toLocaleString() : "—"}
                                            </p>
                                            <p>
                                                IP Address: {device.ipAddress || "—"}
                                            </p>

                                            <div className="release-actions">
                                                <button
                                                    type="button"
                                                    className="danger-btn"
                                                    onClick={() => removeTrustedDevice(device._id)}
                                                >
                                                    Remove Device
                                                </button>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>
                )
            }

            {
                activeTab === "release" && (
                    <div className="command-section release-layout">
                        <div className="command-panel">
                            <h2>Secure Child Release</h2>

                            {emergencyState?.isActive && (
                                <div className="command-banner error" style={{ marginTop: 10 }}>
                                    Emergency mode is active. Only admin/director override releases are allowed, and a reason is required.
                                </div>
                            )}

                            <form className="release-form" onSubmit={submitRelease}>
                                <div
                                    className="student-search-block"
                                    ref={studentSearchRef}
                                    style={{ gridColumn: "1 / -1" }}
                                >
                                    <div className="student-search-input-wrap">
                                        <input
                                            type="text"
                                            placeholder="Search student"
                                            value={studentQuery}
                                            onChange={(e) => {
                                                setStudentQuery(e.target.value);
                                                setShowStudentResults(true);
                                                setHighlightIndex(-1);
                                            }}
                                            onFocus={() => setShowStudentResults(true)}
                                            onKeyDown={(e) => {
                                                if (!studentResults.length) return;

                                                if (e.key === "ArrowDown") {
                                                    e.preventDefault();
                                                    setHighlightIndex((prev) =>
                                                        prev < studentResults.length - 1 ? prev + 1 : prev
                                                    );
                                                }

                                                if (e.key === "ArrowUp") {
                                                    e.preventDefault();
                                                    setHighlightIndex((prev) => (prev > 0 ? prev - 1 : 0));
                                                }

                                                if (e.key === "Enter" && highlightIndex >= 0) {
                                                    e.preventDefault();
                                                    const student = studentResults[highlightIndex];

                                                    handleReleaseChange("studentId", student._id);
                                                    setStudentQuery(
                                                        `${student.firstName || ""} ${student.lastName || ""}`.trim()
                                                    );
                                                    setShowStudentResults(false);
                                                }
                                            }}
                                        />

                                        <ChevronDown
                                            size={16}
                                            className="student-search-chevron"
                                            onClick={() => setShowStudentResults((prev) => !prev)}
                                        />
                                    </div>

                                    {showStudentResults && studentResults.length > 0 && (
                                        <div className="student-search-results">
                                            {studentResults.map((student, index) => (
                                                <button
                                                    key={student._id}
                                                    type="button"
                                                    className={`student-result-item ${index === highlightIndex ? "active" : ""}`}
                                                    onClick={() => {
                                                        const authorizedList = [
                                                            ...(Array.isArray(student.pickups) ? student.pickups : []),
                                                            ...(Array.isArray(student.guardians) ? student.guardians : []),
                                                        ];

                                                        const primaryContact =
                                                            authorizedList.find(
                                                                (person) =>
                                                                    person.primary === true ||
                                                                    person.authorized === true ||
                                                                    person.authorizedPickup === true ||
                                                                    person.isAuthorizedPickup === true
                                                            ) || {};

                                                        handleReleaseChange("studentId", student._id);
                                                        handleReleaseChange("releasedToName", primaryContact.name || "");
                                                        handleReleaseChange("releasedToRelationship", primaryContact.relationship || "");
                                                        handleReleaseChange("releasedToPhone", primaryContact.phone || "");
                                                        handleReleaseChange("releasedToEmail", primaryContact.email || "");

                                                        setStudentQuery(
                                                            `${student.firstName || ""} ${student.lastName || ""}`.trim()
                                                        );
                                                        setShowStudentResults(false);
                                                    }}
                                                >
                                                    <strong>
                                                        {`${student.firstName || ""} ${student.lastName || ""}`.trim()}
                                                    </strong>
                                                    <span>{student.classroom || "No classroom"}</span>
                                                </button>
                                            ))}
                                        </div>
                                    )}
                                </div>

                                <input
                                    type="text"
                                    placeholder="Released to name"
                                    value={releaseForm.releasedToName}
                                    onChange={(e) => handleReleaseChange("releasedToName", e.target.value)}
                                />

                                <input
                                    type="text"
                                    placeholder="Relationship"
                                    value={releaseForm.releasedToRelationship}
                                    onChange={(e) => handleReleaseChange("releasedToRelationship", e.target.value)}
                                />

                                <input
                                    type="text"
                                    placeholder="Phone"
                                    value={releaseForm.releasedToPhone}
                                    onChange={(e) => handleReleaseChange("releasedToPhone", e.target.value)}
                                />

                                <input
                                    type="email"
                                    placeholder="Email"
                                    value={releaseForm.releasedToEmail}
                                    onChange={(e) => handleReleaseChange("releasedToEmail", e.target.value)}
                                />

                                <input
                                    type="text"
                                    placeholder="Pickup code"
                                    value={releaseForm.pickupCode}
                                    onChange={(e) => handleReleaseChange("pickupCode", e.target.value)}
                                />

                                <select
                                    value={releaseForm.verificationMethod}
                                    onChange={(e) => handleReleaseChange("verificationMethod", e.target.value)}
                                >
                                    <option value="manual_verification">Manual Verification</option>
                                    <option value="guardian_match">Guardian Match</option>
                                    <option value="pickup_code">Pickup Code</option>
                                    <option value="staff_override">Staff Override</option>
                                </select>

                                <div className="override-block">
                                    <label className="override-line">
                                        <input
                                            type="checkbox"
                                            checked={releaseForm.override}
                                            onChange={(e) => handleReleaseChange("override", e.target.checked)}
                                        />
                                        <span>Use Override</span>
                                    </label>

                                    {releaseForm.override && (
                                        <input
                                            type="text"
                                            placeholder="Override reason"
                                            value={releaseForm.overrideReason}
                                            onChange={(e) => handleReleaseChange("overrideReason", e.target.value)}
                                        />
                                    )}
                                </div>

                                <textarea
                                    placeholder="Notes"
                                    value={releaseForm.notes}
                                    onChange={(e) => handleReleaseChange("notes", e.target.value)}
                                />

                                <div className="release-actions">
                                    <button type="submit" className="primary-btn">
                                        Complete Release
                                    </button>
                                    <button
                                        type="button"
                                        className="secondary-btn"
                                        onClick={exportReleaseHistory}
                                    >
                                        Export History
                                    </button>
                                </div>
                            </form>
                        </div>

                        <div className="command-panel">
                            <h2>Release History</h2>

                            <div className="history-summary-row">
                                <span className="summary-pill success">
                                    {releaseSummary.completed} Completed
                                </span>
                                <span className="summary-pill danger">
                                    {releaseSummary.blocked} Blocked
                                </span>
                                <span className="summary-pill total">
                                    {releaseSummary.totalMatching} Total
                                </span>
                            </div>

                            <div className="history-legend">
                                <span><span className="legend-dot success"></span> Completed</span>
                                <span><span className="legend-dot danger"></span> Blocked</span>
                            </div>

                            <div className="history-filters">
                                <div className="search-wrap">
                                    <Search size={14} />
                                    <input
                                        type="text"
                                        placeholder="Search student"
                                        value={historyFilters.q}
                                        onChange={(e) => {
                                            setHistoryPage(1);
                                            setHistoryFilters((prev) => ({ ...prev, q: e.target.value }));
                                        }}
                                    />
                                </div>

                                <select
                                    value={historyFilters.status}
                                    onChange={(e) => {
                                        setHistoryPage(1);
                                        setHistoryFilters((prev) => ({ ...prev, status: e.target.value }));
                                    }}
                                >
                                    <option value="">All Statuses</option>
                                    <option value="completed">Completed</option>
                                    <option value="blocked">Blocked</option>
                                </select>

                                <select
                                    value={historyFilters.method}
                                    onChange={(e) => {
                                        setHistoryPage(1);
                                        setHistoryFilters((prev) => ({ ...prev, method: e.target.value }));
                                    }}
                                >
                                    <option value="">All Methods</option>
                                    <option value="pickup_code">Pickup Code</option>
                                    <option value="manual_verification">Manual Verification</option>
                                    <option value="staff_override">Staff Override</option>
                                    <option value="guardian_match">Guardian Match</option>
                                </select>

                                <select
                                    value={historyFilters.overrideUsed}
                                    onChange={(e) => {
                                        setHistoryPage(1);
                                        setHistoryFilters((prev) => ({ ...prev, overrideUsed: e.target.value }));
                                    }}
                                >
                                    <option value="">Override Any</option>
                                    <option value="true">Override Used</option>
                                    <option value="false">No Override</option>
                                </select>
                            </div>

                            <div className="history-list">
                                {releaseHistory.map((item) => (
                                    <div
                                        key={item._id}
                                        className={`history-item ${item.status === "blocked" ? "blocked" : "completed"}`}
                                    >
                                        <div className="history-top">
                                            <strong>
                                                {item.studentId?.name ||
                                                    `${item.studentId?.firstName || ""} ${item.studentId?.lastName || ""}`.trim() ||
                                                    "Student"}
                                            </strong>
                                            <span className={`badge ${item.status === "blocked" ? "danger" : "success"}`}>
                                                {item.status}
                                            </span>
                                        </div>

                                        <p>Released To: {item.releasedTo?.name || "—"}</p>
                                        <p>Method: {item.verification?.method || "—"}</p>
                                        <p>Approved By: {item.approvedBy?.name || item.approvedBy?.email || "—"}</p>
                                        <p>Time: {item.releasedAt ? new Date(item.releasedAt).toLocaleString() : "—"}</p>
                                        {item.notes ? <p>Notes: {item.notes}</p> : null}
                                    </div>
                                ))}
                            </div>

                            <div className="history-pagination">
                                <button
                                    type="button"
                                    className="secondary-btn"
                                    disabled={!releasePagination.hasPrev}
                                    onClick={() => setHistoryPage((prev) => Math.max(prev - 1, 1))}
                                >
                                    Previous
                                </button>

                                <span>
                                    Page {releasePagination.page} of {releasePagination.pages}
                                </span>

                                <button
                                    type="button"
                                    className="secondary-btn"
                                    disabled={!releasePagination.hasNext}
                                    onClick={() => setHistoryPage((prev) => prev + 1)}
                                >
                                    Next
                                </button>
                            </div>
                        </div>
                    </div>
                )
            }

            {
                activeTab === "evacuation" && (
                    <div className="command-section evacuation-layout">
                        <div className="command-panel">
                            <h2>Evacuation Control</h2>

                            {!evacuation?.active ? (
                                <div className="evacuation-start">
                                    <select
                                        value={evacuationType}
                                        onChange={(e) => setEvacuationType(e.target.value)}
                                    >
                                        <option value="drill">Drill</option>
                                        <option value="emergency">Emergency</option>
                                    </select>

                                    <textarea
                                        placeholder="Session notes"
                                        value={evacuationNotes}
                                        onChange={(e) => setEvacuationNotes(e.target.value)}
                                    />

                                    <button className="primary-btn" onClick={startEvacuation} type="button">
                                        Start Evacuation Session
                                    </button>
                                </div>
                            ) : (
                                <div>
                                    <div className="command-cards compact">
                                        <div className="command-card"><h3>Total</h3><strong>{evacuation?.summary?.total ?? 0}</strong></div>
                                        <div className="command-card"><h3>Evacuated</h3><strong>{evacuation?.summary?.evacuated ?? 0}</strong></div>
                                        <div className="command-card"><h3>Missing</h3><strong>{evacuation?.summary?.missing ?? 0}</strong></div>
                                        <div className="command-card"><h3>Pending</h3><strong>{evacuation?.summary?.pending ?? 0}</strong></div>
                                    </div>

                                    <div className="evacuation-list">
                                        {(evacuation?.session?.students || []).map((student) => (
                                            <div key={student.studentId} className={`evacuation-item ${student.status}`}>
                                                <div>
                                                    <strong>{student.name}</strong>
                                                    <p>{student.classroomName || "No classroom"}</p>
                                                </div>

                                                <div className="evac-actions">
                                                    <button
                                                        type="button"
                                                        className="icon-btn success"
                                                        onClick={() => markEvacuationStudent(student.studentId, "evacuated")}
                                                    >
                                                        <CheckCircle2 size={16} />
                                                    </button>
                                                    <button
                                                        type="button"
                                                        className="icon-btn danger"
                                                        onClick={() => markEvacuationStudent(student.studentId, "missing")}
                                                    >
                                                        <XCircle size={16} />
                                                    </button>
                                                    <button
                                                        type="button"
                                                        className="icon-btn"
                                                        onClick={() => markEvacuationStudent(student.studentId, "pending")}
                                                    >
                                                        Reset
                                                    </button>
                                                </div>
                                            </div>
                                        ))}
                                    </div>

                                    <button
                                        className="primary-btn complete-btn"
                                        type="button"
                                        onClick={completeEvacuation}
                                    >
                                        Complete Session
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>
                )
            }

            {
                activeTab === "reports" && (
                    <div className="command-section reports-layout">
                        <div className="command-panel">
                            <h2>Evacuation Reports</h2>
                            <p>Completed drills and emergency evacuation records for reporting and audits.</p>

                            <div className="history-filters">
                                <select
                                    value={evacuationHistoryFilters.type}
                                    onChange={(e) => {
                                        setEvacuationHistoryPage(1);
                                        setEvacuationHistoryFilters((prev) => ({
                                            ...prev,
                                            type: e.target.value,
                                        }));
                                    }}
                                >
                                    <option value="">All Types</option>
                                    <option value="drill">Drill</option>
                                    <option value="emergency">Emergency</option>
                                </select>

                                <select
                                    value={evacuationHistoryFilters.status}
                                    onChange={(e) => {
                                        setEvacuationHistoryPage(1);
                                        setEvacuationHistoryFilters((prev) => ({
                                            ...prev,
                                            status: e.target.value,
                                        }));
                                    }}
                                >
                                    <option value="">All Statuses</option>
                                    <option value="completed">Completed</option>
                                    <option value="cancelled">Cancelled</option>
                                    <option value="active">Active</option>
                                </select>

                                <input
                                    type="date"
                                    value={evacuationHistoryFilters.startDate}
                                    onChange={(e) => {
                                        setEvacuationHistoryPage(1);
                                        setEvacuationHistoryFilters((prev) => ({
                                            ...prev,
                                            startDate: e.target.value,
                                        }));
                                    }}
                                />

                                <input
                                    type="date"
                                    value={evacuationHistoryFilters.endDate}
                                    onChange={(e) => {
                                        setEvacuationHistoryPage(1);
                                        setEvacuationHistoryFilters((prev) => ({
                                            ...prev,
                                            endDate: e.target.value,
                                        }));
                                    }}
                                />
                            </div>

                            <div className="history-list">
                                {evacuationHistory.length === 0 ? (
                                    <div className="empty-state">
                                        No evacuation reports found for selected filters.
                                    </div>
                                ) : (
                                    evacuationHistory.map((session) => (
                                        <div
                                            key={session._id}
                                            className={`history-item ${session.status === "completed" ? "completed" : "blocked"}`}
                                        >
                                            <div className="history-top">
                                                <strong>
                                                    {session.type === "emergency" ? "Emergency Evacuation" : "Drill"}
                                                </strong>
                                                <span className={`badge ${session.status === "completed" ? "success" : "warning"}`}>
                                                    {session.status}
                                                </span>
                                            </div>

                                            <p>
                                                Started: {session.startedAt ? new Date(session.startedAt).toLocaleString() : "—"}
                                            </p>

                                            <p>
                                                Completed: {session.completedAt ? new Date(session.completedAt).toLocaleString() : "—"}
                                            </p>

                                            <p>
                                                Started By: {session.startedBy?.name || session.startedBy?.email || "—"}
                                            </p>

                                            <p>
                                                Totals: {session.summary?.total ?? session.students?.length ?? 0} total ·{" "}
                                                {session.summary?.evacuated ?? 0} evacuated ·{" "}
                                                {session.summary?.missing ?? 0} missing ·{" "}
                                                {session.summary?.pending ?? 0} pending
                                            </p>

                                            {session.notes ? <p>Notes: {session.notes}</p> : null}

                                            <div className="release-actions">
                                                <button
                                                    type="button"
                                                    className="secondary-btn"
                                                    onClick={() => exportEvacuationReport(session._id)}
                                                >
                                                    Export Report
                                                </button>
                                            </div>
                                        </div>
                                    ))
                                )}
                            </div>

                        </div>
                    </div>
                )
            }

            {
                confirmEmergencyType && (
                    <div className="confirm-overlay">
                        <div className="confirm-modal">
                            <h3>
                                {confirmEmergencyType === "evacuation"
                                    ? "Activate Evacuation?"
                                    : "Activate Lockdown?"}
                            </h3>
                            <p>
                                {confirmEmergencyType === "evacuation"
                                    ? "This will activate emergency mode and begin evacuation accountability."
                                    : "This will activate lockdown mode and restrict critical actions like child release."}
                            </p>

                            <div className="confirm-actions">
                                <button
                                    type="button"
                                    className="secondary-btn"
                                    onClick={() => setConfirmEmergencyType("")}
                                >
                                    Cancel
                                </button>

                                <button
                                    type="button"
                                    className={
                                        confirmEmergencyType === "evacuation"
                                            ? "danger-btn"
                                            : "warning-btn"
                                    }
                                    onClick={async () => {
                                        await activateEmergencyMode(confirmEmergencyType);
                                        setConfirmEmergencyType("");
                                    }}
                                >
                                    Confirm
                                </button>
                            </div>
                        </div>
                    </div>
                )
            }

            {loading && <div className="command-loading">Loading command center...</div>}
        </div >

    );
};

export default CenterCommand;